import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-label", "className", "fwdRef", "height", "onDarkBg", "serviceName", "serviceProps", "style"];
import React, { forwardRef } from 'react';
import { color, font } from '@op/opds.tokens';
import { buildVersionedClass, buildVersionedClassNames } from '@op/opds.opux-utilities';
/**
 * _The OP logo mark with a service name._
 *
 * ***
 *
 * The component represents the logo mark together with a service name text.
 * Typically it is used in the site header or top bar.
 *
 * Additional props (including `ref`) will be forwarded to the wrapping `<div>` element.
 */
export const ServiceIdentity = _ref => {
  let {
      'aria-label': ariaLabel,
      className,
      fwdRef,
      height = 48,
      onDarkBg = false,
      serviceName,
      serviceProps,
      style
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  /**
   * From specification https://op-financial.invisionapp.com/d/main#/console/15300369/318080953/preview
   * - Font-size (and line-height) = 60% (0.6) of the logo size
   * - The safety area is the distance of the symbol to the edges of the orange circle times two. With a 100px size
   *   logo mark the distance is 11px (11%, 0.11) based on study
   *
   * Color of the font will vary depending on its size:
   * exact or greater than 27px/2.7rem -> $opds-color-text-hero-default or smaller -> $opds-color-text-default
   */
  const fontScaleRatio = 0.6;
  const pxToRemRatio = 16 * (parseFloat(font.platform.scalefactor) / 100); // For converting the values to rem. 16 is the default browser font size
  const fontSize = height * fontScaleRatio / pxToRemRatio;
  const safetyAreaSize = height * 0.11 * 2 / pxToRemRatio;
  const heightInRems = height / pxToRemRatio + "rem";
  let fontColor = null;
  if (fontSize < 2.7) {
    fontColor = onDarkBg ? color.text.inverse.default : color.text.default;
  } else {
    fontColor = onDarkBg ? color.text.inverse.hero.default : color.text.hero.default;
  }
  return /*#__PURE__*/React.createElement("div", _extends({
    "aria-label": ariaLabel
  }, props, {
    className: buildVersionedClassNames('ds-service-identity', className),
    ref: fwdRef,
    style: _extends({}, style)
  }), /*#__PURE__*/React.createElement("div", {
    className: buildVersionedClass('ds-service-identity__logo-mark'),
    style: {
      height: heightInRems,
      width: heightInRems
    }
  }), /*#__PURE__*/React.createElement("span", _extends({}, serviceProps, {
    className: buildVersionedClass('ds-service-identity__name'),
    style: {
      color: fontColor,
      fontSize: fontSize + "rem",
      lineHeight: heightInRems,
      // To vertically center the text
      marginLeft: safetyAreaSize + "rem",
      paddingLeft: safetyAreaSize + "rem"
    }
  }), serviceName));
};
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(ServiceIdentity, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'ServiceIdentity';
export default Fwd;